<script>
// ConfigurationPage.vue
import { useAlert } from 'dashboard/composables';
import { maskToken } from 'dashboard/helper/tokenMask';
import inboxMixin from 'shared/mixins/inboxMixin';
import SettingsSection from '../../../../../components/SettingsSection.vue';
import ImapSettings from '../ImapSettings.vue';
import SmtpSettings from '../SmtpSettings.vue';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import NextButton from 'dashboard/components-next/button/Button.vue';
import WhatsappReauthorize from '../channels/whatsapp/Reauthorize.vue';
import InstagramReauthorize from '../channels/instagram/Reauthorize.vue';
import FacebookReauthorize from '../channels/facebook/Reauthorize.vue';

import { mapGetters } from 'vuex';

export default {
  components: {
    SettingsSection,
    ImapSettings,
    SmtpSettings,
    NextButton,
    WhatsappReauthorize,
    InstagramReauthorize,
    FacebookReauthorize,
  },
  mixins: [inboxMixin],
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      hmacMandatory: false,
      whatsAppInboxAPIKey: '',
      instagramPageAccessToken: '',
      instagramPageId: '',
      isRequestingReauthorization: false,
      isSyncingTemplates: false,
      facebookPageAccessToken: '',
      facebookPageId: '',
    };
  },
  validations: {
    instagramPageAccessToken: { required },
    instagramPageId: { required },
    whatsAppInboxAPIKey: { required },
    facebookPageAccessToken: { required },
    facebookPageId: { required },
  },
  computed: {
    ...mapGetters({
      uiFlags: 'inboxes/getUIFlags',
    }),
    isAFacebookChannel() {
      return this.inbox?.channel_type === 'Channel::FacebookPage';
    },
    isEmbeddedSignupWhatsApp() {
      return this.inbox.provider_config?.source === 'embedded_signup';
    },
    whatsappAppId() {
      return window.chatwootConfig?.whatsappAppId;
    },
    isAnInstagramChannel() {
      const isInstagramChannel =
        this.inbox?.channel_type === 'Channel::Instagram';
      return isInstagramChannel;
    },
    // Fase 1.7 do plano .ai/messenger-integration-plan.md: nunca renderizar
    // o access_token do Facebook completo em claro na tela — o campo de
    // paste manual abaixo continua funcional como rede de segurança, só a
    // EXIBIÇÃO do token atual é mascarada.
    maskedFacebookAccessToken() {
      return maskToken(this.inbox.access_token);
    },
  },
  watch: {
    inbox() {
      this.setDefaults();
    },
  },
  mounted() {
    this.setDefaults();
  },
  methods: {
    setDefaults() {
      this.hmacMandatory = this.inbox.hmac_mandatory || false;

      // Configurações do Instagram
      if (this.isAnInstagramChannel && this.inbox) {
        this.instagramPageId = this.inbox.instagram_id || '';
        this.instagramPageAccessToken = '';
      }

      if (this.isAFacebookChannel && this.inbox) {
        this.facebookPageId = this.inbox.facebook_id || '';
        this.facebookPageAccessToken = '';
      }
    },
    handleHmacFlag() {
      this.updateInbox();
    },
    async updateInbox() {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            hmac_mandatory: this.hmacMandatory,
          },
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },

    async updateFacebookConfiguration() {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            facebook_id: this.facebookPageId,
            access_token: this.facebookPageAccessToken,
          },
        };

        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
        this.facebookPageAccessToken = '';
      } catch (error) {
        // A mensagem já vem formatada no error.message
        const errorMessage =
          error.message || this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE');
        useAlert(errorMessage);
      }
    },

    async updateInstagramConfiguration() {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            instagram_id: this.instagramPageId,
            access_token: this.instagramPageAccessToken,
          },
        };

        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
        this.instagramPageAccessToken = '';
      } catch (error) {
        // A mensagem já vem formatada no error.message
        const errorMessage =
          error.message || this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE');
        useAlert(errorMessage);
      }
    },

    async updateWhatsAppInboxAPIKey() {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {},
        };

        payload.channel.provider_config = {
          ...this.inbox.provider_config,
          api_key: this.whatsAppInboxAPIKey,
        };

        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    async handleReconfigure() {
      if (this.$refs.whatsappReauth) {
        await this.$refs.whatsappReauth.requestAuthorization();
      }
    },
    async syncTemplates() {
      this.isSyncingTemplates = true;
      try {
        await this.$store.dispatch('inboxes/syncTemplates', this.inbox.id);
        useAlert(
          this.$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_SUCCESS')
        );
      } catch (error) {
        console.log('error: ', error);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      } finally {
        this.isSyncingTemplates = false;
      }
    },
  },
};
</script>

<template>
  <!-- Seções de outros canais (Twilio, Line, etc.) -->
  <div v-if="isATwilioChannel" class="mx-8">
    <SettingsSection
      :title="$t('INBOX_MGMT.ADD.TWILIO.API_CALLBACK.TITLE')"
      :sub-title="$t('INBOX_MGMT.ADD.TWILIO.API_CALLBACK.SUBTITLE')"
    >
      <woot-code :script="inbox.callback_webhook_url" lang="html" />
    </SettingsSection>
  </div>

  <div v-else-if="isALineChannel" class="mx-8">
    <SettingsSection
      :title="$t('INBOX_MGMT.ADD.LINE_CHANNEL.API_CALLBACK.TITLE')"
      :sub-title="$t('INBOX_MGMT.ADD.LINE_CHANNEL.API_CALLBACK.SUBTITLE')"
    >
      <woot-code :script="inbox.callback_webhook_url" lang="html" />
    </SettingsSection>
  </div>

  <div v-else-if="isAWebWidgetInbox">
    <div class="mx-8">
      <SettingsSection
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.MESSENGER_HEADING')"
        :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.MESSENGER_SUB_HEAD')"
      >
        <woot-code
          :script="inbox.web_widget_script"
          lang="html"
          :codepen-title="`${inbox.name} - Cortexwoot Widget Test`"
          enable-code-pen
        />
      </SettingsSection>

      <SettingsSection
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_VERIFICATION')"
      >
        <woot-code :script="inbox.hmac_token" />
        <template #subTitle>
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.HMAC_DESCRIPTION') }}
          <a
            target="_blank"
            rel="noopener noreferrer"
            href="https://www.chatwoot.com/docs/product/channels/live-chat/sdk/identity-validation/"
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.HMAC_LINK_TO_DOCS') }}
          </a>
        </template>
      </SettingsSection>

      <SettingsSection
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_MANDATORY_VERIFICATION')"
        :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_MANDATORY_DESCRIPTION')"
      >
        <div class="flex gap-2 items-center">
          <input
            id="hmacMandatory"
            v-model="hmacMandatory"
            type="checkbox"
            @change="handleHmacFlag"
          />
          <label for="hmacMandatory">
            {{ $t('INBOX_MGMT.EDIT.ENABLE_HMAC.LABEL') }}
          </label>
        </div>
      </SettingsSection>
    </div>
  </div>

  <!-- ConfigurationPage.vue - Seção Instagram completa -->
  <div v-else-if="isAnInstagramChannel" class="mx-8">
    <SettingsSection
      :title="$t('INBOX_MGMT.ADD.INSTAGRAM.INSTAGRAM_ID_SECTION_TITLE')"
      :sub-title="$t('INBOX_MGMT.ADD.INSTAGRAM.INSTAGRAM_ID_SECTION_SUBTITLE')"
    >
      <woot-code :script="inbox.instagram_id || 'N/A'" />
    </SettingsSection>

    <!-- Access Token atual (equivalente à Chave API) -->
    <SettingsSection
      :title="$t('INBOX_MGMT.ADD.INSTAGRAM.ACCESS_TOKEN_SECTION_TITLE')"
      :sub-title="$t('INBOX_MGMT.ADD.INSTAGRAM.ACCESS_TOKEN_SECTION_SUBTITLE')"
    >
      <woot-code :script="inbox.access_token || 'N/A'" />
    </SettingsSection>

    <!-- Atualizar Access Token (equivalente a Atualizar Chave de API) -->
    <SettingsSection
      :title="$t('INBOX_MGMT.ADD.INSTAGRAM.UPDATE_TOKEN_TITLE')"
      :sub-title="$t('INBOX_MGMT.ADD.INSTAGRAM.UPDATE_TOKEN_SUBTITLE')"
    >
      <div
        class="flex flex-1 justify-between items-center mt-2 instagram-settings--content"
      >
        <woot-input
          v-model="instagramPageAccessToken"
          type="text"
          class="flex-1 mr-2 [&>input]:!mb-0"
          :placeholder="
            $t('INBOX_MGMT.ADD.INSTAGRAM.PAGE_ACCESS_TOKEN.PLACEHOLDER')
          "
        />
        <NextButton
          :disabled="v$.instagramPageAccessToken.$invalid || uiFlags.isUpdating"
          :is-loading="uiFlags.isUpdating"
          @click="updateInstagramConfiguration"
        >
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.UPDATE') }}
        </NextButton>
      </div>
    </SettingsSection>
  </div>

  <!-- ConfigurationPage.vue - Seção Facebook completa -->
  <div v-else-if="isAFacebookChannel" class="mx-8">
    <SettingsSection
      :title="$t('INBOX_MGMT.ADD.FB.PAGE_ID_SECTION_TITLE')"
      :sub-title="$t('INBOX_MGMT.ADD.FB.PAGE_ID_SECTION_SUBTITLE')"
    >
      <woot-code :script="inbox.facebook_id || 'N/A'" />
    </SettingsSection>

    <!-- Access Token atual (mascarado — Fase 1.7, nunca em claro na tela) -->
    <SettingsSection
      :title="$t('INBOX_MGMT.ADD.FB.ACCESS_TOKEN_SECTION_TITLE')"
      :sub-title="$t('INBOX_MGMT.ADD.FB.ACCESS_TOKEN_SECTION_SUBTITLE')"
    >
      <woot-code :script="maskedFacebookAccessToken" />
    </SettingsSection>

    <!-- Atualizar Access Token -->
    <SettingsSection
      :title="$t('INBOX_MGMT.ADD.FB.UPDATE_TOKEN_TITLE')"
      :sub-title="$t('INBOX_MGMT.ADD.FB.UPDATE_TOKEN_SUBTITLE')"
    >
      <div
        class="flex flex-1 justify-between items-center mt-2 facebook-settings--content"
      >
        <woot-input
          v-model="facebookPageAccessToken"
          type="text"
          class="flex-1 mr-2 [&>input]:!mb-0"
          :placeholder="$t('INBOX_MGMT.ADD.FB.PAGE_ACCESS_TOKEN.PLACEHOLDER')"
        />
        <NextButton
          :disabled="v$.facebookPageAccessToken.$invalid || uiFlags.isUpdating"
          :is-loading="uiFlags.isUpdating"
          @click="updateFacebookConfiguration"
        >
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.UPDATE') }}
        </NextButton>
      </div>
    </SettingsSection>
  </div>

  <!-- Resto das seções (API Inbox, Email, WhatsApp, etc.) -->
  <div v-else-if="isAPIInbox" class="mx-8">
    <SettingsSection
      :title="$t('INBOX_MGMT.SETTINGS_POPUP.INBOX_IDENTIFIER')"
      :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.INBOX_IDENTIFIER_SUB_TEXT')"
    >
      <woot-code :script="inbox.inbox_identifier" />
    </SettingsSection>

    <SettingsSection
      :title="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_VERIFICATION')"
      :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_DESCRIPTION')"
    >
      <woot-code :script="inbox.hmac_token" />
    </SettingsSection>

    <SettingsSection
      :title="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_MANDATORY_VERIFICATION')"
      :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_MANDATORY_DESCRIPTION')"
    >
      <div class="flex gap-2 items-center">
        <input
          id="hmacMandatory"
          v-model="hmacMandatory"
          type="checkbox"
          @change="handleHmacFlag"
        />
        <label for="hmacMandatory">
          {{ $t('INBOX_MGMT.EDIT.ENABLE_HMAC.LABEL') }}
        </label>
      </div>
    </SettingsSection>
  </div>

  <div v-else-if="isAnEmailChannel">
    <div class="mx-8">
      <SettingsSection
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.FORWARD_EMAIL_TITLE')"
        :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.FORWARD_EMAIL_SUB_TEXT')"
      >
        <woot-code :script="inbox.forward_to_email" />
      </SettingsSection>
    </div>
    <ImapSettings :inbox="inbox" />
    <SmtpSettings v-if="inbox.imap_enabled" :inbox="inbox" />
  </div>

  <div v-else-if="isAWhatsAppChannel && !isATwilioChannel">
    <div v-if="inbox.provider_config" class="mx-8">
      <!-- Embedded Signup Section -->
      <template v-if="isEmbeddedSignupWhatsApp">
        <SettingsSection
          v-if="whatsappAppId"
          :title="
            $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_EMBEDDED_SIGNUP_TITLE')
          "
          :sub-title="
            $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_EMBEDDED_SIGNUP_SUBHEADER')
          "
        >
          <div class="flex gap-4 items-center">
            <p class="text-sm text-slate-600">
              {{
                $t(
                  'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_EMBEDDED_SIGNUP_DESCRIPTION'
                )
              }}
            </p>
            <NextButton @click="handleReconfigure">
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_RECONFIGURE_BUTTON') }}
            </NextButton>
          </div>
        </SettingsSection>
      </template>

      <!-- Manual Setup Section -->
      <template v-else>
        <SettingsSection
          :title="$t('INBOX_MGMT.ADD.WHATSAPP.CALLBACK_WEBHOOK_URL')"
          :sub-title="
            $t('INBOX_MGMT.ADD.WHATSAPP.CALLBACK_WEBHOOK_URL_SUBTITLE')
          "
        >
          <woot-code :script="inbox.callback_webhook_url" />
        </SettingsSection>

        <SettingsSection
          :title="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_WEBHOOK_TITLE')"
          :sub-title="
            $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_WEBHOOK_SUBHEADER')
          "
        >
          <woot-code :script="inbox.provider_config.webhook_verify_token" />
        </SettingsSection>

        <SettingsSection
          :title="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_TITLE')"
          :sub-title="
            $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_SUBHEADER')
          "
        >
          <woot-code :script="inbox.provider_config.api_key" />
        </SettingsSection>

        <SettingsSection
          :title="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_TITLE')"
          :sub-title="
            $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_SUBHEADER')
          "
        >
          <div
            class="flex flex-1 justify-between items-center mt-2 whatsapp-settings--content"
          >
            <woot-input
              v-model="whatsAppInboxAPIKey"
              type="text"
              class="flex-1 mr-2 [&>input]:!mb-0"
              :placeholder="
                $t(
                  'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_PLACEHOLDER'
                )
              "
            />
            <NextButton
              :disabled="v$.whatsAppInboxAPIKey.$invalid"
              @click="updateWhatsAppInboxAPIKey"
            >
              {{
                $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON')
              }}
            </NextButton>
          </div>
        </SettingsSection>
      </template>

      <SettingsSection
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_TITLE')"
        :sub-title="
          $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_SUBHEADER')
        "
      >
        <div class="flex justify-start items-center mt-2">
          <NextButton :disabled="isSyncingTemplates" @click="syncTemplates">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_BUTTON') }}
          </NextButton>
        </div>
      </SettingsSection>
    </div>

    <WhatsappReauthorize
      v-if="isEmbeddedSignupWhatsApp"
      ref="whatsappReauth"
      :inbox="inbox"
      class="hidden"
    />
  </div>
</template>

<style lang="scss" scoped>
.whatsapp-settings--content {
  ::v-deep input {
    margin-bottom: 0;
  }
}
</style>
